/* eslint-disable react/prop-types */
/**
 * RestroPulse POS · Sprint 4 · Manager authorisation + exception reporting.
 *
 * All state derived from the parent's reducer — no fetch, no persistence.
 *
 * • ManagerPinDialog is a SHARED authorisation surface. Every gated
 *   action opens it with an `action_label` and a callback taking the
 *   authoriser id + timestamp. A wrong PIN shows an inline error and
 *   does NOT close the dialog (so an operator retries in place).
 *
 * • ExceptionReport is the single "loss / wastage / fraud risk" surface,
 *   sourced from state.orders + state.session. Everything a regional
 *   manager wants to see across voids, comps, discounts, cancellations
 *   and variances lives here, grouped by type AND by authoriser.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ShieldCheck, AlertTriangle, Printer } from "lucide-react";
import { POS_CONFIG } from "./posConfig";
import { ORDER_STATE, computeTotalsFor } from "./posReducer";
import { aggregateSales } from "./Sprint3";

const fmt2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ═══════════════════════════════════════════════════════════════════
// Part 5 · Shared Manager PIN dialog
// ═══════════════════════════════════════════════════════════════════

/**
 * SHARED authorisation surface. Opens with an `action_label` describing
 * what is being authorised, and calls `onConfirm({ authorised_by, at })`
 * on the correct PIN. Wrong PIN → inline error, dialog stays open.
 *
 * Real production issues per-manager PINs from a directory. The
 * prototype checks against POS_CONFIG.manager_pin (mock "1234").
 */
import { STAFF, findStaffByPin } from "./posReducer";
import axios from "axios";
const API = process.env.REACT_APP_BACKEND_URL;

export function ManagerPinDialog({ open, action_label, reason_prompt, initiatorStaffId, onCancel, onConfirm }) {
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setPin(""); setReason(""); setError(null); setBusy(false);
    setTimeout(() => inputRef.current?.focus(), 40);
  }, [open, action_label]);

  const submit = async () => {
    if (busy) return;
    // Prefer the live server path when a POS token exists — a manager
    // must resolve against `pos_staff` for the BOUND outlet and be
    // verified against the hashed PIN, not against a demo constant.
    const posToken = typeof window !== "undefined"
      ? window.localStorage.getItem("pos_token") : null;
    if (posToken) {
      setBusy(true); setError(null);
      try {
        let terminal_id = "unbound";
        try {
          const b = JSON.parse(window.localStorage.getItem("pos_terminal_binding") || "null");
          if (b?.terminal_id) terminal_id = b.terminal_id;
        } catch (_) { /* keep default */ }
        const r = await axios.post(`${API}/api/pos/auth/verify-manager-pin`,
          { pin, initiator_staff_id: initiatorStaffId || null },
          { headers: {
              Authorization: `Bearer ${posToken}`,
              "X-Pos-Terminal": terminal_id,
              "Content-Type": "application/json",
            } });
        onConfirm({
          authorised_by:    r.data.authorised_by,
          authorised_by_id: r.data.authorised_by_id,
          at:               Date.now(),
          reason_code:      reason.trim() || null,
        });
      } catch (e) {
        const msg = e?.response?.data?.detail
          || "Incorrect PIN — please try again.";
        setError(msg);
        setTimeout(() => inputRef.current?.focus(), 20);
      } finally { setBusy(false); }
      return;
    }
    // Demo fallback — mock staff lookup. Only reached when there is
    // no POS session (i.e. /pos-demo).
    const staff = findStaffByPin(pin);
    if (!staff || staff.role !== "manager" || !staff.active) {
      setError(staff && staff.role !== "manager"
        ? `${staff.name} is a ${staff.role}. Manager role required.`
        : "Incorrect PIN — please try again.");
      setTimeout(() => inputRef.current?.focus(), 20);
      return;
    }
    if (initiatorStaffId && staff.id === initiatorStaffId) {
      setError("Cashiers cannot authorise their own action. Ask a different manager.");
      setTimeout(() => inputRef.current?.focus(), 20);
      return;
    }
    onConfirm({
      authorised_by:    staff.name,
      authorised_by_id: staff.id,
      at:               Date.now(),
      reason_code:      reason.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[440px] w-[92vw] p-0 gap-0 border border-slate-300 overflow-hidden"
        data-testid="pos-pin-dialog">
        <DialogHeader className="px-5 py-3 border-b border-slate-100 bg-slate-50 space-y-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-slate-700" /> Manager authorisation
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-700">
            {action_label}
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">Manager PIN</label>
            <input ref={inputRef} type="password" value={pin}
              onChange={(e) => { setPin(e.target.value); setError(null); }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") { e.preventDefault(); submit(); }
                if (e.key === "Escape") { e.preventDefault(); onCancel(); }
              }}
              inputMode="numeric"
              placeholder="••••"
              className={`w-full h-12 rounded border px-3 text-[22px] tabular-nums tracking-widest text-center focus:outline-none
                ${error ? "border-rose-500 bg-rose-50" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white focus:border-slate-500"}`}
              data-testid="pos-pin-input"
              maxLength={6} />
            {error && (
              <div className="text-[11px] text-rose-800 mt-1 font-medium" data-testid="pos-pin-error">
                {error}
              </div>
            )}
          </div>
          {reason_prompt && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">{reason_prompt}</label>
              <input type="text" value={reason}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="e.g. GUEST_COMPLAINT, PRICE_ERROR, MANAGER_DISCRETION"
                className="w-full h-9 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white px-2 text-[12px] focus:outline-none focus:border-slate-500"
                data-testid="pos-pin-reason" />
            </div>
          )}
          <div className="text-[10px] italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            Records authoriser and timestamp on the event.
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-pin-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Cancel
          </button>
          <button type="button" onClick={submit}
            disabled={pin.length < 4}
            data-testid="pos-pin-confirm"
            className="h-10 px-4 rounded bg-slate-800 text-white text-[12px] font-semibold hover:bg-slate-900 disabled:opacity-40">
            Authorise
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Part 7 · Exception report
// ═══════════════════════════════════════════════════════════════════

/**
 * Single loss / wastage / fraud-risk surface. Sourced from live state.
 * Sections grouped by TYPE, and a matching "By authoriser" cross-tab.
 */
export function ExceptionReport({ state, onClose }) {
  const sales = useMemo(() => aggregateSales(state), [state]);
  // POS Control Sprint · guard for percentage division. When gross
  // sales is effectively zero (early business day, all-PRINTED-no-
  // settlement scenarios), display "—" instead of a misleading 6800 %
  // reading. `hasGross` gates the pct helper below.
  const hasGross = Number(sales.gross || 0) > 0.005;
  const gross_sales = sales.gross || 1;

  const voided_orders = useMemo(() => {
    return Object.values(state.orders)
      .filter((o) => o.order.state === ORDER_STATE.VOIDED)
      .map((o) => {
        const t = computeTotalsFor(state, o.order.id);
        return {
          order_id:      o.order.id,
          invoice_no:    o.order.invoice_no,
          value:         t.grand_total,
          reason:        o.order.void_reason,
          authorised_by: o.order.void_authorised_by,
          at:            o.order.void_at,
        };
      });
  }, [state]);

  const voided_invoices  = state.session.invoice_voids       || [];
  const invoice_replacements = state.session.invoice_replacements || {};
  const cancellations    = state.session.ticket_cancellations || [];
  const shifts           = state.session.shifts               || [];

  const discounts = useMemo(() => Object.values(state.orders)
    // POS Control Sprint · post-print discount audit — the discount
    // must appear here as soon as it is applied, even before the
    // eventual re-settlement. Widening past SETTLED-only captures
    // the "PRINTED → void → discount → new invoice" trail.
    .filter((o) => o.order.state !== ORDER_STATE.VOIDED && o.order.discount)
    .map((o) => {
      const t = computeTotalsFor(state, o.order.id);
      return {
        order_id:      o.order.id,
        invoice_no:    o.order.invoice_no,
        type:          o.order.discount.type,
        value:         t.discount,
        reason:        o.order.discount.reason,
        authorised_by: o.order.discount.authorised_by,
        at:            o.order.opened_at,
      };
    }), [state]);

  const comps = useMemo(() => Object.values(state.orders)
    .filter((o) => o.order.state === ORDER_STATE.SETTLED
      && (o.payments || []).some((p) => p.mode === "complimentary"))
    .map((o) => {
      const compAmount = (o.payments || []).filter((p) => p.mode === "complimentary")
        .reduce((s, p) => s + Number(p.amount || 0), 0);
      return {
        order_id:      o.order.id,
        invoice_no:    o.order.invoice_no,
        value:         compAmount,
        reason:        o.order.comp_reason || "MANAGER_DISCRETION",
        authorised_by: o.order.comp_authorised_by || "system",
        at:            o.order.opened_at,
      };
    }), [state]);

  const opens = (sales.opens || []).map((o) => ({
    item:          o.line.item_name,
    value:         Number(o.line.qty || 0) * Number(o.line.unit_price || 0),
    punched_by:    o.line.punched_by,
    order_id:      o.order.id,
  }));

  const reprints = state.session.reprints || [];

  const variances = shifts.filter((s) => s.closed_at && Math.abs(Number(s.variance || 0)) > 0.005)
    .map((s) => ({
      shift_id:      s.id,
      value:         Number(s.variance),
      reason:        s.reason_code,
      closed_by:     s.closed_by,
      at:            s.closed_at,
    }));

  // Sprint 4 correction 3 · EDC variance is tracked separately from
  // cash variance and never netted against it. A cash short of ₹400
  // and an EDC over of ₹400 remain TWO exceptions, not zero.
  const edcVariances = shifts.filter((s) => s.closed_at && Math.abs(Number(s.edc_variance || 0)) > 0.005)
    .map((s) => ({
      shift_id:      s.id,
      value:         Number(s.edc_variance),
      reason:        s.edc_reason_code,
      closed_by:     s.closed_by,
      at:            s.closed_at,
    }));

  // Sprint 4 correction 6 · Any reopen of a closed business day
  // surfaces here with authoriser, reason and timestamp.
  const dayReopens = state.session.business_day_reopens || [];

  const total_exception_value = [
    ...voided_orders,
    ...voided_invoices.map((v) => ({ value: Number(v.value || 0) })),
    ...cancellations.map((c) => ({ value: Number(c.value || (c.qty_cancelled * (c.unit_price || 0)) || 0) })),
    ...discounts,
    ...comps,
    ...variances.map((v) => ({ value: Math.abs(Number(v.value || 0)) })),
    ...edcVariances.map((v) => ({ value: Math.abs(Number(v.value || 0)) })),
  ].reduce((s, x) => s + Number(x.value || 0), 0);

  const pct = (v) => hasGross ? `${((v / gross_sales) * 100).toFixed(1)}%` : "—";

  // By authoriser cross-tab — same manager appearing across voids,
  // comps and discounts is exactly the pattern this report exists to
  // surface.
  const byAuthoriser = useMemo(() => {
    const m = {};
    const add = (a, kind, v) => {
      if (!a) return;
      if (!m[a]) m[a] = { voids: 0, invoices: 0, cancels: 0, discounts: 0, comps: 0, total: 0 };
      m[a][kind] += Number(v || 0);
      m[a].total += Number(v || 0);
    };
    voided_orders.forEach((v)   => add(v.authorised_by, "voids",     v.value));
    voided_invoices.forEach((v) => add(v.authorised_by, "invoices",  v.value));
    cancellations.forEach((c)   => add(c.authorised_by, "cancels",   c.qty_cancelled * (c.unit_price || 0)));
    discounts.forEach((d)       => add(d.authorised_by, "discounts", d.value));
    comps.forEach((c)           => add(c.authorised_by, "comps",     c.value));
    return m;
  }, [voided_orders, voided_invoices, cancellations, discounts, comps]);

  const sumSection = (rows) => rows.reduce((s, r) => s + Number(r.value || 0), 0);

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[900px] w-[95vw] max-h-[95vh] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid="pos-exception-report">
        <DialogHeader className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] shrink-0 space-y-0 bg-amber-50">
          <DialogTitle className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700" /> Exception report
          </DialogTitle>
          <DialogDescription className="text-[11px] text-amber-900">
            Loss · wastage · fraud risk. Read this first.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5 text-[12px] leading-relaxed">
          {/* Summary strip */}
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 mb-4"
            data-testid="pos-exception-summary">
            <div className="text-[10px] uppercase tracking-wider text-amber-900 font-semibold">Total exception value</div>
            <div className="flex items-baseline justify-between">
              <div className="text-[32px] font-semibold tabular-nums text-amber-900" data-testid="pos-exception-total">
                ₹{fmt2(total_exception_value)}
              </div>
              <div className="text-[16px] tabular-nums text-amber-800" data-testid="pos-exception-pct">
                {pct(total_exception_value)} of gross sales
              </div>
            </div>
            <div className="text-[11px] text-amber-800 mt-1">
              Gross sales <span className="tabular-nums font-medium">₹{fmt2(sales.gross)}</span>
            </div>
          </div>

          <Section title="1 · Voided orders" rows={voided_orders} totalPct={pct} sum={sumSection} testid="pos-exc-voided-orders"
            columns={["Invoice", "Value", "Reason", "Authoriser", "Time"]}
            renderRow={(r, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{r.invoice_no || r.order_id}</td>
                <td className="text-right tabular-nums">₹{fmt2(r.value)}</td>
                <td>{r.reason || "—"}</td>
                <td className="tabular-nums">{r.authorised_by || "—"}</td>
                <td className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">{r.at ? new Date(r.at).toLocaleTimeString() : "—"}</td>
              </tr>
            )} />

          <Section title="2 · Voided invoices" rows={voided_invoices} totalPct={pct} sum={sumSection} testid="pos-exc-voided-invoices"
            columns={["Voided invoice", "Value", "Reason", "Authoriser", "Replaced by"]}
            renderRow={(v, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{v.voided_invoice}</td>
                <td className="text-right tabular-nums">₹{fmt2(v.value || 0)}</td>
                <td>{v.reason_code || "—"}</td>
                <td className="tabular-nums">{v.authorised_by || "—"}</td>
                <td className="tabular-nums">{(invoice_replacements[v.voided_invoice] || []).join(", ") || "—"}</td>
              </tr>
            )} />

          <Section title="3 · Cancelled ticket items" rows={cancellations} totalPct={pct}
            sum={(rows) => rows.reduce((s, c) => s + Number(c.qty_cancelled || 0) * Number(c.unit_price || 0), 0)}
            testid="pos-exc-cancellations"
            columns={["Item", "Qty", "Reason", "Authoriser", "Time", "Fired"]}
            renderRow={(c, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td>{c.item_name}</td>
                <td className="text-right tabular-nums">{c.qty_cancelled}</td>
                <td>{c.reason_code || "—"}</td>
                <td className="tabular-nums">{c.authorised_by || "—"}</td>
                <td className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">{c.at ? new Date(c.at).toLocaleTimeString() : "—"}</td>
                <td>{c.ticket_id ? "yes" : "no"}</td>
              </tr>
            )} />

          <Section title="4 · Discounts" rows={discounts} totalPct={pct} sum={sumSection} testid="pos-exc-discounts"
            columns={["Invoice", "Type", "Value", "Reason", "Authoriser"]}
            renderRow={(d, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{d.invoice_no || d.order_id}</td>
                <td>{d.type}</td>
                <td className="text-right tabular-nums">₹{fmt2(d.value)}</td>
                <td>{d.reason || "—"}</td>
                <td className="tabular-nums">{d.authorised_by || "—"}</td>
              </tr>
            )} />

          <Section title="5 · Complimentary settlements" rows={comps} totalPct={pct} sum={sumSection} testid="pos-exc-comps"
            columns={["Invoice", "Value", "Reason", "Authoriser"]}
            renderRow={(c, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{c.invoice_no || c.order_id}</td>
                <td className="text-right tabular-nums">₹{fmt2(c.value)}</td>
                <td>{c.reason || "—"}</td>
                <td className="tabular-nums">{c.authorised_by || "—"}</td>
              </tr>
            )} />

          <Section title="6 · Open items punched" rows={opens} totalPct={pct} sum={sumSection} testid="pos-exc-opens"
            columns={["Item", "Value", "Punched by"]}
            renderRow={(o, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td>{o.item}</td>
                <td className="text-right tabular-nums">₹{fmt2(o.value)}</td>
                <td className="tabular-nums">{o.punched_by || "—"}</td>
              </tr>
            )} />

          <Section title="7 · Reprints" rows={reprints} totalPct={() => ""} sum={() => 0} testid="pos-exc-reprints"
            columns={["Bill", "Count", "Reason", "Notes", "Manager", "Reprinted by"]}
            renderRow={(r, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{r.invoice_no}</td>
                <td className="text-right tabular-nums">{r.count || 1}</td>
                <td>{r.reason_code || "—"}</td>
                <td className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">{r.notes || "—"}</td>
                <td className="tabular-nums">{r.authorised_by || "—"}</td>
                <td className="tabular-nums">{r.reprinted_by || "—"}</td>
              </tr>
            )} />

          <Section title="8 · Cash variance" rows={variances} totalPct={pct}
            sum={(rows) => rows.reduce((s, v) => s + Math.abs(Number(v.value || 0)), 0)}
            testid="pos-exc-variance"
            columns={["Shift", "Variance", "Reason", "Closed by", "Time"]}
            renderRow={(v, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{v.shift_id}</td>
                <td className={`text-right tabular-nums ${v.value < 0 ? "text-rose-800" : "text-emerald-800"}`}>
                  {v.value >= 0 ? "+" : ""}₹{fmt2(v.value)}
                </td>
                <td>{v.reason || "—"}</td>
                <td className="tabular-nums">{v.closed_by || "—"}</td>
                <td className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">{v.at ? new Date(v.at).toLocaleTimeString() : "—"}</td>
              </tr>
            )} />

          <Section title="8b · EDC batch variance" rows={edcVariances} totalPct={pct}
            sum={(rows) => rows.reduce((s, v) => s + Math.abs(Number(v.value || 0)), 0)}
            testid="pos-exc-edc-variance"
            columns={["Shift", "Variance", "Reason", "Closed by", "Time"]}
            renderRow={(v, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{v.shift_id}</td>
                <td className={`text-right tabular-nums ${v.value < 0 ? "text-rose-800" : "text-emerald-800"}`}>
                  {v.value >= 0 ? "+" : ""}₹{fmt2(v.value)}
                </td>
                <td>{v.reason || "—"}</td>
                <td className="tabular-nums">{v.closed_by || "—"}</td>
                <td className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">{v.at ? new Date(v.at).toLocaleTimeString() : "—"}</td>
              </tr>
            )} />

          <Section title="9 · Business-day reopens" rows={dayReopens} totalPct={() => ""}
            sum={() => 0}
            testid="pos-exc-day-reopens"
            columns={["Reopened date", "Was closed", "Authoriser", "Reason", "Time"]}
            renderRow={(r, i) => (
              <tr key={i} className="border-b border-[#EEF0EC]">
                <td className="tabular-nums">{r.business_date}</td>
                <td className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">{r.last_closed_before || "—"}</td>
                <td className="tabular-nums">{r.authorised_by || "—"}</td>
                <td>{r.reason_code || "—"}{r.note ? ` · ${r.note}` : ""}</td>
                <td className="tabular-nums text-[color:var(--pos-color-secondary-text,#5C6B64)]">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
              </tr>
            )} />

          {/* By authoriser cross-tab */}
          <div className="mt-5" data-testid="pos-exception-by-authoriser">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold border-b border-[color:var(--pos-color-border,#E2E8DF)] pb-1 mb-1.5">
              By authoriser · the pattern this report exists to surface
            </div>
            {Object.keys(byAuthoriser).length === 0
              ? <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)]">No authorised exceptions on record.</div>
              : (
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] border-b border-[color:var(--pos-color-border,#E2E8DF)]">
                      <th className="text-left py-1">Manager</th>
                      <th className="text-right py-1">Order voids</th>
                      <th className="text-right py-1">Invoice voids</th>
                      <th className="text-right py-1">Cancels</th>
                      <th className="text-right py-1">Discounts</th>
                      <th className="text-right py-1">Comps</th>
                      <th className="text-right py-1">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(byAuthoriser).map(([mgr, row]) => (
                      <tr key={mgr} className="border-b border-[#EEF0EC]" data-testid={`pos-exc-auth-${mgr}`}>
                        <td className="tabular-nums font-medium">{mgr}</td>
                        <td className="text-right tabular-nums">₹{fmt2(row.voids)}</td>
                        <td className="text-right tabular-nums">₹{fmt2(row.invoices)}</td>
                        <td className="text-right tabular-nums">₹{fmt2(row.cancels)}</td>
                        <td className="text-right tabular-nums">₹{fmt2(row.discounts)}</td>
                        <td className="text-right tabular-nums">₹{fmt2(row.comps)}</td>
                        <td className="text-right tabular-nums font-semibold">₹{fmt2(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 shrink-0">
          <button type="button" disabled
            title="Export is UI-only in this sprint"
            data-testid="pos-exception-report-export"
            className="h-9 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px] text-[#B8B8B5] cursor-not-allowed">
            Export
          </button>
          <button type="button" disabled
            data-testid="pos-exception-report-print"
            className="h-9 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px] text-[#B8B8B5] cursor-not-allowed inline-flex items-center gap-1">
            <Printer className="w-3 h-3" /> Print
          </button>
          <button type="button" onClick={onClose}
            data-testid="pos-exception-report-close"
            className="h-9 px-3 rounded bg-[color:var(--color-primary)] text-white text-[11px] font-semibold hover:bg-[color:var(--color-primary-hover)]">
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, rows, columns, renderRow, sum, totalPct, testid }) {
  const total = sum(rows);
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-semibold border-b border-[color:var(--pos-color-border,#E2E8DF)] pb-1 mb-1.5 flex items-baseline justify-between">
        <span>{title} · {rows.length}</span>
        <span className="tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)] normal-case">
          {total > 0 ? <>₹{fmt2(total)} <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">({totalPct(total)})</span></> : "—"}
        </span>
      </div>
      {rows.length === 0
        ? <div className="italic text-[color:var(--pos-color-secondary-text,#5C6B64)] text-[11px]">Nothing to report.</div>
        : (
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                {columns.map((c) => (
                  <th key={c} className={c === "Value" || c === "Qty" || c === "Count" || c === "Variance"
                    ? "text-right py-0.5"
                    : "text-left py-0.5"}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>{rows.map(renderRow)}</tbody>
          </table>
        )
      }
    </div>
  );
}
